import React, { useState, useEffect } from 'react';
import { LucideSidebarIcons } from './LucideSidebarIcons';
import { CircleChevronDown } from 'lucide-react';
import { LogOut } from 'lucide-react';

const ALWAYS_EXPANDABLE_SECTIONS = new Set(['Operations', 'Setup', 'Lists', 'Reports']);

// `collapsed` is owned by App.js (its toggle now lives at the top of the
// module rail, not in this panel's own header) — fully hides this panel
// rather than shrinking it to an icon rail, since the module rail already
// covers icon-only quick navigation.
function Sidebar({ moduleKey, moduleLabel, navSections, currentPage, onNavClick, isOpen, collapsed, navCounts = {} }) {
  const [expandedSection, setExpandedSection] = useState({});

  useEffect(() => {
    const savedState = localStorage.getItem(`sidebar_expanded_${moduleKey}`);
    if (savedState) {
      setExpandedSection(JSON.parse(savedState));
      return;
    }

    // Defaults to every section expanded — this panel is reached by
    // clicking a rail icon specifically to see the full page list, not to
    // browse an accordion one section at a time.
    const initialState = navSections.reduce((accumulator, section) => {
      accumulator[section.header] = true;
      return accumulator;
    }, {});
    setExpandedSection(initialState);
  }, [moduleKey, navSections]);

  const toggleSection = (section) => {
    const newState = {
      ...expandedSection,
      [section]: !expandedSection[section],
    };
    setExpandedSection(newState);
    localStorage.setItem(`sidebar_expanded_${moduleKey}`, JSON.stringify(newState));
  };

  return (
    <div className={`sidebar ${isOpen && !collapsed ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        <div className="sidebar-header-text">
          <h2>{moduleLabel}</h2>
          <p className="sidebar-subtitle">Module Navigation</p>
        </div>
      </div>

      <nav className="sidebar-nav">
        {navSections.map((section) => {
          const shouldRenderExpandable = section.items.length > 1 || ALWAYS_EXPANDABLE_SECTIONS.has(section.header);
          const isSingleDestination = section.items.length === 1 && !shouldRenderExpandable;
          const singleItem = section.items[0];

          if (isSingleDestination) {
            const LucideIcon = LucideSidebarIcons[singleItem.label] || LucideSidebarIcons.Default;
            return (
              <div key={section.header} className="nav-section">
                <button
                  className={`nav-section-toggle nav-section-direct ${currentPage === singleItem.pageKey ? 'active' : ''}`}
                  onClick={() => onNavClick(singleItem.pageKey)}
                  aria-current={currentPage === singleItem.pageKey ? 'page' : undefined}
                >
                  <span className="nav-icon">
                    <LucideIcon size={20} strokeWidth={1.5} />
                  </span>
                  <span className="section-title">{section.header}</span>
                </button>
              </div>
            );
          }

          const SectionIcon = LucideSidebarIcons[section.header];

          return (
            <div key={section.header} className="nav-section">
              <button
                className="nav-section-toggle"
                onClick={() => toggleSection(section.header)}
                aria-expanded={expandedSection[section.header]}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {SectionIcon && (
                    <span className="nav-icon" style={{ marginRight: 0 }}>
                      <SectionIcon size={18} strokeWidth={1.5} />
                    </span>
                  )}
                  <span className="section-title">{section.header}</span>
                </span>
                <span className="toggle-icon" style={{ transition: 'transform 0.3s ease' }}>
                  <CircleChevronDown size={16} strokeWidth={2} style={{ transform: expandedSection[section.header] ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                </span>
              </button>

              <div className={`nav-section-menu ${expandedSection[section.header] ? '' : 'collapsed'}`}>
                {section.items.map((item, index) => {
                  const LucideIcon = LucideSidebarIcons[item.label] || LucideSidebarIcons.Default;
                  return (
                    <button
                      key={index}
                      className={`nav-item ${currentPage === item.pageKey ? 'active' : ''}`}
                      onClick={() => onNavClick(item.pageKey)}
                    >
                      <span className="nav-icon">
                        <LucideIcon size={20} strokeWidth={1.5} />
                      </span>
                      <span className="nav-label">{item.label}</span>
                      {navCounts[item.pageKey] > 0 && (
                        <span className="nav-count" title={`${navCounts[item.pageKey]} portal orders awaiting acceptance`}>{navCounts[item.pageKey]}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <button className="logout-btn">
          <span style={{ verticalAlign: 'middle', marginRight: 6 }}>
            <LogOut size={18} strokeWidth={1.5} />
          </span>
          <span className="nav-label">Logout</span>
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
